import { useState } from 'react';
import { Send, X, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getBaseUrl } from '../config/apiConfig';
import csrfService from '../utils/csrfService';

const REJECTION_REASONS = [
  { value: 'test', labelKey: 'feedback.reasonTestLabel', descKey: 'feedback.reasonTestDesc' },
  { value: 'quality', labelKey: 'feedback.reasonQualityLabel', descKey: 'feedback.reasonQualityDesc' },
  { value: 'incomplete', labelKey: 'feedback.reasonIncompleteLabel', descKey: 'feedback.reasonIncompleteDesc' },
  { value: 'wrong', labelKey: 'feedback.reasonWrongLabel', descKey: 'feedback.reasonWrongDesc' },
  { value: 'other', labelKey: 'feedback.reasonOtherLabel', descKey: 'feedback.reasonOtherDesc' },
];

function FeedbackForm({ loggedInUser, onClose, onSubmitSuccess, folder }) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalComment, setAdditionalComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async () => {
    if (!selectedReason) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const reasonObj = REJECTION_REASONS.find(r => r.value === selectedReason);
      const reasonLabel = reasonObj ? t(reasonObj.labelKey) : selectedReason;
      const csrfToken = await csrfService.ensureToken();

      // 1. Reject the project (set status to rejected, delete server files)
      if (folder && folder !== 'general-feedback') {
        await fetch(`${getBaseUrl()}/api/python-backend/projects/${folder}/reject`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          credentials: 'include',
          body: JSON.stringify({ reason: reasonLabel, comment: additionalComment }),
        });
      }

      // 2. Send feedback email
      const response = await fetch(`${getBaseUrl()}/api/user/sendFeedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({
          folder: folder || 'general-feedback',
          feedbackReason: reasonLabel,
          feedbackText: additionalComment,
          user: loggedInUser
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          if (onSubmitSuccess) onSubmitSuccess();
          else if (onClose) onClose();
        }, 1500);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Feedback error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">{t('feedback.thankYou')}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('feedback.helpImprove')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Reason Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          {t('feedback.whyReject')}
        </label>
        <div className="space-y-2">
          {REJECTION_REASONS.map((reason) => (
            <label
              key={reason.value}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                selectedReason === reason.value
                  ? 'border-[#0f7699] bg-[#0f7699]/5 dark:border-[#4fc3f7] dark:bg-[#4fc3f7]/10'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="rejection-reason"
                value={reason.value}
                checked={selectedReason === reason.value}
                onChange={(e) => {
                  setSelectedReason(e.target.value);
                  setSubmitStatus(null);
                }}
                className="mt-0.5 w-4 h-4 text-[#0f7699] border-slate-300 focus:ring-[#0f7699] dark:border-slate-600"
              />
              <div className="flex-1">
                <span className="block text-sm font-medium text-slate-800 dark:text-white">
                  {t(reason.labelKey)}
                </span>
                <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {t(reason.descKey)}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Comment */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {t('feedback.additionalNotes')} <span className="text-slate-400 font-normal">({t('feedback.optional')})</span>
        </label>
        <textarea
          value={additionalComment}
          onChange={(e) => setAdditionalComment(e.target.value)}
          placeholder={t('feedback.describeProblem')}
          rows={3}
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#0f7699]/30 dark:focus:ring-[#4fc3f7]/30 resize-none"
        />
      </div>

      {/* Error Message */}
      {submitStatus === 'error' && !selectedReason && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-600 dark:text-red-400">{t('feedback.selectReasonError')}</span>
        </div>
      )}

      {submitStatus === 'error' && selectedReason && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-600 dark:text-red-400">{t('feedback.sendError')}</span>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="flex-1 py-3 px-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl font-medium text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          {t('feedback.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 py-3 px-4 bg-[#0f7699] dark:bg-[#4fc3f7] text-white rounded-xl font-medium text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>{t('feedback.sending')}</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>{t('feedback.submit')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default FeedbackForm;
